import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import _ from 'lodash';

import InitialState from './data/InitialState';
import DefaultState from './data/DefaultState';

import { ERROR, PRICE_SELECTED, QUOTED, START } from 'pu-data/QuoteSteps';
import SimpleReactValidator from 'pu-utils/simple-react-validator';

import { getQuote, emailQuote } from 'api/apiQuote';

import { selectAssemblyDataForQuote, selectAssemblyDataForValidate } from './selector/selector';
import { changeTab } from 'product_tabs/tabs/tabSlice';
import { TAB_ASSEMBLY } from 'product_tabs/tabs/data/tabType';
import { showErrors } from 'product_tabs/alerts/alertSlice';

import { changeGlobalParameter } from 'product_tabs/actions';
import { isParameterNameQtyValid, isProductTypeParameterNameQtyValid } from 'pu-utils/validate';

export const doQuote = createAsyncThunk(
  'assembly/doQuote',
  async (args, thunkAPI) => {
    const { variant, parameters, suppliers } = selectAssemblyDataForQuote(thunkAPI.getState());
    const { row_selected, column_selected, supplier_selected, vendor_selected, ...rest } = parameters;
    const token = document.getElementsByName('authenticity_token')[0].value;
    const data = {};
    suppliers.forEach((s) => {
      data[s] = rest;
    });

    const promise = getQuote({
      data, token, variant, quoteNumber: parameters.product_quote_number,
    });

    return promise
      .then((res) => res.data)
      .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
  },
);

export const doEmailQuote = createAsyncThunk('assembly/doEmailQuote', async (args, thunkAPI) => {
  const { data, token } = args;
  const promise = emailQuote({ data, token });
  return promise
    .then((res) => res.data)
    .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
});

export const validator = new SimpleReactValidator({});

export const projectValidator = new SimpleReactValidator({});

const assemblyService = createSlice({
  name: 'assemblyService',
  initialState: {
    ...InitialState,
    validatorFlag: false,
  },
  reducers: {
    changeParameter(state, action) {
      const { name, value } = action.payload

      var quantityTo
      switch (name) {
        case 'qty1':
          state.parameters.quantities[0] = value
          break;
        case 'qty2':
          state.parameters.quantities[1] = value
          break;
        case 'qty3':
          state.parameters.quantities[2] = value
          break;
        case 'qty4':
          state.parameters.quantities[3] = value
          break;
        default:
          state.parameters[name] = value
      }

      if (name !== 'part_number' && name !== 'revision' && name !== 'filename') {
        state.parametersChanged = true
      }

      if (isParameterNameQtyValid(name)) {
        if (!state.quantitiesChanged) {
          state.quantitiesChanged = true
        }

        // Update the USA quantities matrix
        if (state.quote.line_items.length > 0 && state.quoteStep == START) {
          const qty_index = Number(name.split('qty')[1]) - 1
          state.quote.line_items[0]['quantities'][qty_index] = value
        }
      }
    },
    setPriceSelected(state, action) {
      const { row_selected, column_selected, supplier, currentVariant } = action.payload
      state.parameters.row_selected = row_selected
      state.parameters.column_selected = column_selected
      state.parameters.supplier_selected = supplier
      state.currentVariant = currentVariant
      state.quoteStep = PRICE_SELECTED
    },
    deleteAssembly(state) {
      const { part_number, revision, filename } = state.parameters;
      validator.hideMessages();
      const newState = _.cloneDeep({ ...DefaultState, validatorFlag: false });
      newState.parameters.part_number = part_number;
      newState.parameters.revision = revision;
      newState.parameters.filename = filename;
      newState.lineItemId = state.lineItemId;
      newState.destroy = true;
      newState.zip_file_input_id = state.zip_file_input_id;
      return newState;
    },
    updateValidatorFlag(state) {
      state.validatorFlag = !state.validatorFlag;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(changeGlobalParameter, (state, action) => {
        const { name, value } = action.payload
        if (['part_number', 'revision'].includes(name)) {
          state.parameters[name] = value;
        } else if(name == 'zip_input_id'){
          state.zip_file_input_id = value
        } else if(isProductTypeParameterNameQtyValid(name, 'fabrication') && !state.quantitiesChanged && state.quoteStep == START) {
          const qty_index = Number(name.split('_qty')[1]) - 1
          state.parameters.quantities[qty_index] = value
          state.quote.line_items[0]['quantities'][qty_index] = value
        }
      })
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.loading = false;
        state.parametersChanged = false;

        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.supplier_selected = '';

        if (!quote || !quote.line_items) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        state.quote = quote;
      })
      .addCase(doQuote.rejected, (state) => {
        state.loading = false;
        state.quote = {};
        state.quoteStep = ERROR;
      })
      .addCase(doEmailQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doEmailQuote.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(doEmailQuote.rejected, (state, action) => {
        state.loading = false;
      })
  },
})

export const {
  changeParameter,
  setPriceSelected,
  updateValidatorFlag,
  deleteAssembly,
} = assemblyService.actions;

export default assemblyService.reducer;

export const validate = () => (dispatch, getState) => {
  const { quoteStep, parametersChanged } = selectAssemblyDataForValidate(getState())

  let validateResult = true;

  if (quoteStep === PRICE_SELECTED) {
    if (!validator.allValid()) {
      validator.showMessages();
      dispatch(updateValidatorFlag());
      dispatch(changeTab(TAB_ASSEMBLY));
      dispatch(showErrors('Please fix the errors in Assembly form'));
      validateResult = false;
      setTimeout(() => {
        document.querySelector('.pcb-assembly .srv-validation-message').scrollIntoView(false);
      }, 500);
    } else {
      if (parametersChanged) {
        dispatch(showErrors('One or more parameters have changed. Please get a new Assembly quote'));
        dispatch(changeTab(TAB_ASSEMBLY));
        validateResult = false;
      }
    }
  }

  return validateResult;
}